import { Controller } from "@hotwired/stimulus"
import { EditorView, ViewPlugin, Decoration, keymap, placeholder as cmPlaceholder } from "@codemirror/view"
import { EditorState, RangeSetBuilder } from "@codemirror/state"
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands"
import { autocompletion, completionKeymap, acceptCompletion, startCompletion } from "@codemirror/autocomplete"

function liquidTokenHighlighter(validTokens) {
  const validSet = new Set(validTokens)

  return ViewPlugin.fromClass(class {
    constructor(view) { this.decorations = this.build(view) }
    update(update) {
      if (update.docChanged || update.viewportChanged) this.decorations = this.build(update.view)
    }
    build(view) {
      const builder = new RangeSetBuilder()
      const doc = view.state.doc.toString()
      const re = /\{\{.*?\}\}/g
      let match
      while ((match = re.exec(doc)) !== null) {
        const inner = match[0].slice(2, -2).trim()
        const cls = validSet.has(inner) ? "cm-liquid-token" : "cm-liquid-token-invalid"
        builder.add(match.index, match.index + match[0].length,
          Decoration.mark({ class: cls }))
      }
      return builder.finish()
    }
  }, { decorations: v => v.decorations })
}

const theme = EditorView.theme({
  "&": {
    fontSize: "0.875rem",
    fontFamily: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
    borderRadius: "0.5rem",
    border: "1px solid var(--color-border)",
    backgroundColor: "var(--color-background)",
    transition: "border-color 0.15s, box-shadow 0.15s",
  },
  "&.cm-focused": {
    outline: "none",
    borderColor: "var(--color-primary)",
    boxShadow: "0 0 0 2px color-mix(in srgb, var(--color-primary) 50%, transparent)",
  },
  ".cm-scroller": { overflow: "auto" },
  ".cm-content": {
    padding: "0.5rem 0",
    caretColor: "var(--color-foreground)",
    color: "var(--color-foreground)",
  },
  ".cm-line": { padding: "0 0.75rem" },
  ".cm-cursor": { borderLeftColor: "var(--color-foreground)" },
  ".cm-placeholder": { display: "inline-block", height: "0", overflow: "visible" },
  ".cm-selectionBackground": { backgroundColor: "color-mix(in srgb, var(--color-primary) 25%, transparent) !important" },
  ".cm-liquid-token": {
    color: "var(--color-primary)",
    fontWeight: "600",
    backgroundColor: "color-mix(in srgb, var(--color-primary) 10%, transparent)",
    borderRadius: "3px",
    padding: "0 2px",
  },
  ".cm-liquid-token-invalid": {
    color: "var(--color-destructive)",
    fontWeight: "600",
    backgroundColor: "color-mix(in srgb, var(--color-destructive) 10%, transparent)",
    borderRadius: "3px",
    padding: "0 2px",
    textDecoration: "underline wavy var(--color-destructive)",
  },
  ".cm-tooltip-autocomplete": {
    backgroundColor: "var(--color-card)",
    border: "1px solid var(--color-border)",
    borderRadius: "0.5rem",
    overflow: "hidden",
    boxShadow: "var(--shadow-md)",
  },
  ".cm-tooltip-autocomplete ul li": {
    padding: "4px 8px",
    fontSize: "0.8125rem",
    fontFamily: "ui-monospace, SFMono-Regular, monospace",
    color: "var(--color-foreground)",
  },
  ".cm-completionDetail": {
    fontFamily: "system-ui, sans-serif",
    fontStyle: "normal",
    opacity: "0.6",
    fontSize: "0.75rem",
    marginLeft: "0.5em",
  },
  ".cm-tooltip-autocomplete ul li[aria-selected]": {
    backgroundColor: "var(--color-primary)",
    color: "var(--color-primary-foreground)",
  },
})

function liquidCompletionSource(topLevel, byPrefix) {
  return function(context) {
    const pos = context.pos
    const doc = context.state.doc.toString()

    const before = doc.slice(Math.max(0, pos - 50), pos)
    const braceIdx = before.lastIndexOf("{{")
    if (braceIdx === -1) return null

    const between = before.slice(braceIdx)
    if (between.includes("}}")) return null

    const inner = between.slice(2).trimStart()
    const from = Math.max(0, pos - 50) + braceIdx

    if (inner.length === 0) {
      return { from, options: topLevel, filter: true }
    }

    // Any typing — show full attribute completions, scoped by prefix if possible
    const prefix = inner.split(".")[0].trim()
    const prefixOptions = byPrefix[prefix]
    if (prefixOptions) {
      return { from, options: prefixOptions, filter: true }
    }

    const all = Object.values(byPrefix).flat()
    return { from, options: all, filter: true }
  }
}

export default class extends Controller {
  static values = { tokens: Array, placeholder: String, multiline: { type: Boolean, default: false } }
  static targets = ["input"]

  connect() {
    const textarea = this.inputTarget
    textarea.style.display = "none"

    // Build two-tier completions: top-level prefixes and per-prefix attribute lists
    // Tokens are either strings or {name, description} objects
    const prefixes = new Set()
    const byPrefix = {}
    const tokenNames = []
    for (const t of this.tokensValue) {
      const name = typeof t === "string" ? t : t.name
      const desc = typeof t === "string" ? null : t.description
      const [prefix] = name.split(".")
      prefixes.add(prefix)
      tokenNames.push(name)
      const option = { label: `{{ ${name} }}`, type: "variable" }
      if (desc) option.detail = desc.replace(/\s+/g, " ").trim().slice(0, 100)
      ;(byPrefix[prefix] ||= []).push(option)
    }
    const topLevel = [...prefixes].map(p => ({
      label: `{{ ${p}`,
      apply: `{{ ${p}.`,
      type: "namespace",
    }))

    const completionSource = liquidCompletionSource(topLevel, byPrefix)

    const extensions = [
      keymap.of([
        { key: "Tab", run: acceptCompletion },
        { key: "Enter", run: acceptCompletion },
        ...completionKeymap,
        ...defaultKeymap,
        ...historyKeymap,
      ]),
      history(),
      autocompletion({
        override: [completionSource],
        activateOnTyping: true,
      }),
      liquidTokenHighlighter(tokenNames),
      EditorView.lineWrapping,
      theme,
      EditorView.updateListener.of(update => {
        if (update.docChanged) {
          textarea.value = update.state.doc.toString()
          textarea.dispatchEvent(new Event("input", { bubbles: true }))

          // Trigger completion when {{ is typed
          const pos = update.state.selection.main.head
          const doc = update.state.doc.toString()
          if (pos >= 2 && doc.slice(pos - 2, pos) === "{{") {
            const view = update.view
            setTimeout(() => startCompletion(view), 1)
          }
        }
      }),
    ]

    if (this.placeholderValue) {
      extensions.push(cmPlaceholder(this.placeholderValue))
    }

    if (this.multilineValue) {
      extensions.push(EditorView.theme({
        ".cm-content": { minHeight: "16rem" },
        ".cm-scroller": { minHeight: "16rem" },
      }))
    } else {
      extensions.push(EditorState.transactionFilter.of(tr => {
        if (tr.newDoc.lines > 1) return []
        return tr
      }))
    }

    this.editor = new EditorView({
      state: EditorState.create({
        doc: textarea.value,
        extensions,
      }),
      parent: this.element,
    })
  }

  disconnect() {
    this.editor?.destroy()
  }
}
